import { rentalContractStatusLabel } from "../tenant/RentalContractSection";
import { keyReceiptStatusLabel } from "../tenant/KeyReceiptSection";
import { tenancyDocumentCategoryLabel } from "../tenant/TenancyDocumentsSection";
import { deliveryListLabel } from "../tenant/termination/terminationConfirmationDeliveryModel";

/**
 * The tenancy's documents as ONE list — a presentation layer, not a claim
 * about the database.
 *
 * There is no `documents` table behind a tenancy. Five separate families are
 * tenancy-scoped, each with its own table, lifecycle and PDF pipeline:
 *
 *   rental_contract           RentalContract           tenancies/{id}/rental-contracts
 *   termination_confirmation  TerminationConfirmation  tenancies/{id}/termination/confirmations
 *   key_receipt               KeyReceipt               tenancies/{id}/key-receipts
 *   inspection                InventoryInspection      inventory/inspections?tenancy_id
 *   upload                    TenancyDocument          tenancy-documents?tenancy_id
 *
 * What follows normalises those five row shapes into one row shape so the tab
 * can render a quiet list instead of five differently-dressed sections. It
 * invents no common entity and no common id: every item keeps its `source`,
 * and the open action dispatches on it back to that family's own endpoint.
 *
 * KND-4b: termination confirmations are read, never projected. They are not
 * TenancyDocument rows and never become one; each stored version is listed
 * from the confirmations GET it already has, and opens through that
 * family's own signed-URL endpoint.
 *
 * ONE DOCUMENT, ONE ROW (M1c-B, M1c-C). Since a signed key receipt and a signed
 * handover protocol are each archived as a TenancyDocument pointing at the very
 * same signed PDF, the same file can now reach this list twice — once as the
 * record, once as the archived document. `projectedSourceKeys` resolves that by
 * source identity, not by category or filename, and only for records that
 * actually have a projection. Identity is what makes it safe: move_in and
 * move_out share one category with each other and with a hand-uploaded
 * protocol, so nothing coarser could tell the three apart.
 *
 * PURE ON PURPOSE. No fetching, no React — the mapping is the part worth
 * testing on its own, and a wrong German label or a swapped date is a bug you
 * want to see without a rendered tree around it.
 */

/** Reading order, not data order: the contract that constitutes the tenancy
 *  first, then the confirmation of its termination (a contract document of
 *  the same kind, KND-4b), then the handover paperwork, then whatever was
 *  uploaded by hand. The four older families keep their order among
 *  themselves. */
const SOURCE_RANK = {
  rental_contract: 0,
  termination_confirmation: 1,
  key_receipt: 2,
  inspection: 3,
  upload: 4,
};

/** How a stored confirmation version relates to the termination as it is now
 *  (the server's `lifecycle`). */
const CONFIRMATION_LIFECYCLE = {
  current: "Aktuell",
  superseded: "Ersetzt",
  previous_termination: "Frühere Kündigung",
  termination_withdrawn: "Kündigung zurückgenommen",
};

/** German document titles per inspection type.
 *
 *  move_in is an Übergabe, move_out an Abnahme — two legally distinct
 *  documents out of one pipeline. Same pair the backend's
 *  `protocol_title_de` emits into the PDF itself, so the list and the
 *  document it opens never disagree on what the document is called. */
const INSPECTION_TITLE = {
  move_in: "Übergabeprotokoll",
  move_out: "Abnahmeprotokoll",
};

function asArray(value) {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.items)) return value.items;
  return [];
}

/** Rental contracts. `display_reference` is the contract's own number — the
 *  string the reference design shows under "Mietvertrag". */
export function normalizeRentalContracts(rows) {
  return asArray(rows).map((c) => ({
    key: `rental_contract:${c.id}`,
    source: "rental_contract",
    id: String(c.id),
    title: "Mietvertrag",
    subtitle: [c.display_reference, c.version ? `Version ${c.version}` : null]
      .filter(Boolean)
      .join(" · "),
    statusLabel: rentalContractStatusLabel(c.status),
    dateIso: c.generated_at || c.created_at || null,
    // `has_pdf` is the server's own answer to "is there a file". Offering an
    // open button without it would produce a 404 in a fresh tab.
    canOpen: Boolean(c.has_pdf),
  }));
}

/**
 * Termination confirmations (KND-4b), from the confirmations GET.
 *
 * EVERY version, one row each — the current one, the replaced ones, those of
 * an earlier termination and of a withdrawn one. Nothing is filtered to
 * "current": the history is the point of keeping old versions readable. The
 * number runs on per tenancy across termination acts (act A: v1, v2 — act
 * B: v3, …; KND-3b), so no two rows share one; the act itself is told by the
 * server's `lifecycle`.
 *
 * The delivery label reads the version's own history (newest attempt first,
 * as the server sends it); no request per version. No projection: the
 * uploads list is not consulted, and nothing here is ever written anywhere.
 * Every stored version has its PDF — the server stores the file before it
 * stores the version — so each row offers to be opened.
 */
export function normalizeTerminationConfirmations(data) {
  const versions = Array.isArray(data?.versions) ? data.versions : [];
  return versions
    .filter((v) => v && v.id)
    .map((v) => ({
      key: `termination_confirmation:${v.id}`,
      source: "termination_confirmation",
      id: String(v.id),
      title: "Kündigungsbestätigung",
      subtitle: v.version ? `Version ${v.version}` : "",
      // The lifecycle and the delivery state are what a reader scans these
      // rows for, so they go to the second line, which wraps; the first line
      // is truncated on a phone and keeps only the version and the date.
      statusLabel: "",
      lifecycleLabel: CONFIRMATION_LIFECYCLE[v.lifecycle] || "",
      dateIso: v.generated_at || null,
      detail: [CONFIRMATION_LIFECYCLE[v.lifecycle], deliveryListLabel(v)]
        .filter(Boolean)
        .join(" · "),
      // Several versions share one title, so the button names the version
      // (unique per tenancy: it runs on across termination acts, KND-3b) and
      // its standing — current, replaced, earlier act, withdrawn — which a
      // screen reader user otherwise only hears on the line above.
      openLabel: [
        "Kündigungsbestätigung",
        v.version ? `Version ${v.version}` : null,
        CONFIRMATION_LIFECYCLE[v.lifecycle] || null,
      ]
        .filter(Boolean)
        .join(", ")
        .concat(" öffnen"),
      version: Number.isFinite(Number(v.version)) ? Number(v.version) : null,
      isCurrent: v.lifecycle === "current",
      canOpen: true,
    }));
}

/**
 * The source identities that are already archived as TenancyDocument rows.
 *
 * A projection (M1c-A) carries `source_entity_type` + `source_entity_id`; a
 * hand-uploaded file carries neither. Keying on that pair — and on nothing else
 * — is what keeps a manually uploaded file in the `key_receipt` CATEGORY from
 * suppressing an actual key receipt: the category says what a document is, the
 * source pair says which record it mirrors.
 */
export function projectedSourceKeys(uploads) {
  const keys = new Set();
  for (const d of asArray(uploads)) {
    const type = d?.source_entity_type;
    const id = d?.source_entity_id;
    if (type == null || type === "" || id == null || id === "") continue;
    keys.add(`${String(type)}:${String(id)}`);
  }
  return keys;
}

/**
 * Key receipts, minus the ones the tenancy already lists as archived documents.
 *
 * THREE STATES, and the difference between the last two is why this cannot be
 * `if (signed) skip`:
 *
 *   draft / generated      the record's own row, opening the generated PDF
 *   signed + projected     suppressed — the projection IS the document row
 *   signed, not projected  the record's own row, opening the SIGNED PDF
 *
 * The third case is the whole reason `projected` is passed in. Signed receipts
 * that predate M1c-B have no projection and will not get one until the M1c-D
 * backfill runs; skipping every signed receipt would make those documents
 * vanish from the tenancy until then. So a row is suppressed only when its
 * exact source identity is present among the uploads.
 *
 * A signed receipt also stops offering the generated PDF: once a signature
 * exists, that file is a superseded draft, and handing it over as "the
 * document" would present an unsigned page as the signed original.
 */
export function normalizeKeyReceipts(rows, projected = new Set()) {
  const out = [];
  for (const r of asArray(rows)) {
    if (!r) continue;
    if (projected.has(`key_receipt:${String(r.id)}`)) continue;
    const signed = String(r.status || "") === "signed";
    out.push({
      key: `key_receipt:${r.id}`,
      source: "key_receipt",
      id: String(r.id),
      title: "Schlüsselquittung",
      subtitle: r.handover_location || "",
      statusLabel: keyReceiptStatusLabel(r.status),
      dateIso: r.receipt_date || r.created_at || null,
      // Which of the record's two PDFs this row stands for. The opener reads
      // it; `canOpen` is the existence check on that one file, never the other.
      // A signed receipt whose signed key is missing therefore offers nothing —
      // rather than quietly falling back to the unsigned draft.
      pdfVariant: signed ? "signed" : "generated",
      canOpen: Boolean(signed ? r.signed_pdf_object_key : r.pdf_object_key),
    });
  }
  return out;
}

/**
 * Move-in / move-out inspections, minus the ones already archived as documents.
 *
 * FOUR STATES, and the difference between the last two is why this cannot be
 * `if (signed) skip`:
 *
 *   draft / finalised      the record's own row, opening the protocol PDF
 *   signed + projected     suppressed — the projection IS the document row
 *   signed, not projected  the record's own row, opening the SIGNED PDF
 *   signed, no signed key  the record's own row, opening nothing
 *
 * The third case is the whole reason `projected` is passed in. Signed
 * inspections that predate M1c-C have no projection and will not get one until
 * the M1c-D backfill runs; skipping every signed inspection would make those
 * protocols vanish from the tenancy until then. So a row is suppressed only
 * when its exact source identity is present among the uploads — never by
 * category, which move_in and move_out share with each other AND with a manual
 * upload.
 *
 * A signed inspection also stops offering the protocol PDF: once a signature
 * exists, that file is a superseded draft, and handing it over as "the
 * document" would present an unsigned page as the signed original.
 */
export function normalizeInspections(rows, projected = new Set()) {
  const out = [];
  for (const i of asArray(rows)) {
    if (!i) continue;
    if (projected.has(`inspection:${String(i.id)}`)) continue;
    const signed = Boolean(i.signed_at);
    out.push({
      key: `inspection:${i.id}`,
      source: "inspection",
      id: String(i.id),
      title: INSPECTION_TITLE[i.type] || "Protokoll",
      subtitle: i.inspected_by || "",
      // The ladder the inspection actually walks: draft -> finalised -> signed.
      statusLabel: signed
        ? "Unterzeichnet"
        : i.finalized_at
          ? "Abgeschlossen"
          : "Entwurf",
      dateIso: i.inspection_date || i.created_at || null,
      // Which of the record's two PDFs this row stands for. The opener reads
      // it; `canOpen` is the existence check on that one file, never the other.
      // A signed inspection whose signed key is missing therefore offers
      // nothing — rather than quietly falling back to the unsigned protocol.
      pdfVariant: signed ? "signed" : "generated",
      canOpen: Boolean(signed ? i.signed_pdf_object_key : i.protocol_pdf_object_key),
    });
  }
  return out;
}

/** Files uploaded against the tenancy by hand. */
export function normalizeTenancyUploads(rows) {
  return asArray(rows).map((d) => ({
    key: `upload:${d.id}`,
    source: "upload",
    id: String(d.id),
    // The file IS the document here — there is no generated title to use.
    title: d.file_name || "Dokument",
    subtitle: d.uploaded_by_name || "",
    statusLabel: tenancyDocumentCategoryLabel(d.category),
    dateIso: d.created_at || null,
    // An upload always has a file; that is what an upload is.
    canOpen: true,
  }));
}

/** Two confirmation versions generated at the same instant: the higher
 *  version first. The id only decides between two rows with the SAME number,
 *  which the contract rules out (UNIQUE (tenancy_id, version), KND-3b) — it is
 *  there so malformed data still sorts deterministically, not because such
 *  rows are expected. Every other family keeps its order on a tie. */
function confirmationTieBreak(a, b) {
  if (a.source !== "termination_confirmation" || b.source !== a.source) return 0;
  const byVersion = (b.version ?? -1) - (a.version ?? -1);
  if (byVersion !== 0) return byVersion;
  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
}

/**
 * The five normalised lists as one, in reading order and newest first inside
 * each family. A family that failed to load is simply absent — the caller
 * names it separately rather than having this function guess.
 */
export function buildTenancyDocuments({
  contracts,
  terminationConfirmations,
  keyReceipts,
  inspections,
  uploads,
} = {}) {
  // Computed once from the uploads, which are the only family that can carry a
  // source pair. Key receipts (M1c-B) and inspections (M1c-C) consume it;
  // contracts and termination confirmations have no projection and do not.
  const projected = projectedSourceKeys(uploads);
  const items = [
    ...normalizeRentalContracts(contracts),
    ...normalizeTerminationConfirmations(terminationConfirmations),
    ...normalizeKeyReceipts(keyReceipts, projected),
    ...normalizeInspections(inspections, projected),
    ...normalizeTenancyUploads(uploads),
  ];
  return items.sort((a, b) => {
    const rank = SOURCE_RANK[a.source] - SOURCE_RANK[b.source];
    if (rank !== 0) return rank;
    // Newest first; an undated row sorts last rather than to the top.
    if (!a.dateIso && !b.dateIso) return confirmationTieBreak(a, b);
    if (!a.dateIso) return 1;
    if (!b.dateIso) return -1;
    if (a.dateIso < b.dateIso) return 1;
    if (a.dateIso > b.dateIso) return -1;
    return confirmationTieBreak(a, b);
  });
}
